import { translateUiText } from './runtime';

const textSources = new WeakMap<Text, string>();
const attributeSources = new WeakMap<Element, Map<string, string>>();
const LOCALIZED_ATTRIBUTES = ['aria-label', 'aria-description', 'placeholder', 'title', 'alt'] as const;

function shouldSkip(node: Node) {
  const parent = node instanceof Element ? node : node.parentElement;
  return Boolean(parent?.closest([
    'script',
    'style',
    'textarea',
    '[contenteditable="true"]',
    '[data-i18n-skip]',
    '.customer-chat-message.is-bubble p',
    '.carrier-chat-message.is-bubble p',
  ].join(',')));
}

function localizeTextNode(node: Text) {
  if (shouldSkip(node)) return;
  const current = node.nodeValue ?? '';
  let source = textSources.get(node);
  if (/[\u0400-\u04ff]/u.test(current) && current !== source) {
    source = current;
    textSources.set(node, source);
  }
  if (!source) return;

  const localized = translateUiText(source);
  if (localized !== current) node.nodeValue = localized;
}

function localizeElementAttributes(element: Element) {
  if (shouldSkip(element)) return;
  let sources = attributeSources.get(element);

  for (const attribute of LOCALIZED_ATTRIBUTES) {
    const current = element.getAttribute(attribute);
    if (!current) continue;
    let source = sources?.get(attribute);
    if (/[\u0400-\u04ff]/u.test(current) && current !== source) {
      source = current;
      sources ??= new Map<string, string>();
      sources.set(attribute, source);
      attributeSources.set(element, sources);
    }
    if (!source) continue;
    const localized = translateUiText(source);
    if (localized !== current) element.setAttribute(attribute, localized);
  }
}

function localizeTree(root: Node) {
  if (root instanceof Text) {
    localizeTextNode(root);
    return;
  }
  if (root instanceof Element) localizeElementAttributes(root);

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    if (current instanceof Text) localizeTextNode(current);
    else if (current instanceof Element) localizeElementAttributes(current);
    current = walker.nextNode();
  }
}

export function localizeDocument() {
  if (document.body) localizeTree(document.body);
}

export function observeLocalizedDocument() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        localizeElementAttributes(mutation.target as Element);
        continue;
      }
      for (const node of mutation.addedNodes) localizeTree(node);
      if (mutation.type === 'characterData') localizeTextNode(mutation.target as Text);
    }
  });

  observer.observe(document.body, {
    attributeFilter: [...LOCALIZED_ATTRIBUTES],
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true,
  });
  localizeDocument();
  return () => observer.disconnect();
}
